/**
 * AviumLogo — the canonical brand mark.
 *
 * Single source of truth for the in-app logo. Two render variants so the
 * mark stays legible on both light and dark surfaces:
 *
 *  - variant="default" (light surfaces): indigo squircle + white "A" +
 *    orange crossbar. Used on white modals, white cards, the favicon.
 *
 *  - variant="dark" (dark/indigo chrome): NO squircle — just the white
 *    "A" letterform + orange crossbar floating on the chrome. The
 *    chrome itself frames the mark, so the squircle is redundant and
 *    actually loses visibility when it sits on a similar-hue indigo
 *    background (the original problem this variant solves). Decision
 *    locked 2026-05-23 — see ~/Desktop/avium-logo-dark-variant.html
 *    for the comparison.
 *
 * If you ever swap the design again, edit this file + the matching
 * SVGs at client/public/favicon.svg and client/public/logos/avium-mark.svg
 * (those keep the squircle since favicons and standalone marks sit on
 * indeterminate backgrounds).
 */
type Variant = 'default' | 'dark';

interface Props {
  size?: number;
  className?: string;
  /** Accessible name override. Defaults to "Avium". */
  title?: string;
  /** Render variant. Use 'dark' on indigo / dark chrome surfaces. */
  variant?: Variant;
}

export default function AviumLogo({ size = 32, className, title = 'Avium', variant = 'default' }: Props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      width={size}
      height={size}
      role="img"
      aria-label={title}
      className={className}
    >
      {variant === 'default' && (
        <rect width="64" height="64" rx="14" fill="#0F766E" />
      )}
      <path
        d="M14 52 L32 12 L50 52"
        fill="none"
        stroke="#FFFFFF"
        strokeWidth="9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect x="17" y="32" width="30" height="7" rx="3.5" fill="#E8705A" />
    </svg>
  );
}
